import React, {useEffect, useRef, useReducer, useState} from 'react';
import PropTypes from 'prop-types';
import {Motion, spring, presets} from 'react-motion';
import InstrumentOptions from './InstrumentOptions';
import ResizeHandle from './ResizeHandle';
import {INSTRUMENT_MAP} from 'utils';

const INSTRUMENT_OPTIONS_LEFT_MARGIN_MAP = Object.freeze({
  fgen: 10,
  vps: 10,
  dmm: 10,
  scope: 10,
  bode: 10,
  iv: 10,
  datalogger: 10,
  datagenerator: 10,
  digitalio: 10,
});

const springConfig = presets.stiff;
const defaultStyle = {
  scale: spring(1, springConfig),
  shadow: spring(1, springConfig),
};
const activeStyle = {
  scale: spring(1.05, springConfig),
  shadow: spring(16, springConfig),
};

const useDragAndDropReducer = ({originalLeft, originalTop}) => {
  const initialState = {
    overlayActivated: false,
    isPressing: false,
    mouseX: 0,
    mouseY: 0,
    deltaX: 0,
    deltaY: 0,
  };
  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'ACTIVATE':
        return {
          ...state,
          overlayActivated: true,
          isPressing: true,
          deltaX: action.deltaX,
          deltaY: action.deltaY,
          mouseX: action.mouseX,
          mouseY: action.mouseY,
        };
      case 'ACTIVATE_OVERLAY':
        return {
          ...state,
          overlayActivated: action.activated,
        };
      case 'SET_MOUSE_POSITION':
        return {
          ...state,
          mouseX: action.mouseX,
          mouseY: action.mouseY,
        };
      case 'DEACTIVATE':
        return {...initialState};
      default:
        return state;
    }
  }, initialState);

  const handleActivate = ({pageX, pageY}) =>
    dispatch({
      type: 'ACTIVATE',
      deltaX: pageX - originalLeft,
      deltaY: pageY - originalTop,
      mouseX: pageX,
      mouseY: pageY,
    });

  const setMousePosition = ({pageX, pageY}) =>
    dispatch({
      type: 'SET_MOUSE_POSITION',
      mouseX: pageX,
      mouseY: pageY,
    });

  const handleDeactivate = () => dispatch({type: 'DEACTIVATE'});

  const setOverlayActivated = (activated) => dispatch({type: 'ACTIVATE_OVERLAY', activated});

  return {
    state,
    handleActivate,
    setMousePosition,
    handleDeactivate,
    setOverlayActivated,
  };
};

const Instrument = (props) => {
  const {
    order,
    originalLeft,
    originalTop,
    instrumentLength,
    instrument: {name, size, widthDiff, heightDiff, topDiff, settings},
    style,
    reorderInstruments,
    resizeInstrument,
    popoutInstrument,
    isHorizontal,
    isLargeScreen,
    isPopoutMode,
    isSimulateMode,
    preconfigured,
    updateNoScrollMinHeight,
    setReorderingSize,
    scrollContainerRef,
    hasInstrumentOptions,
  } = props;
  const {
    state: {overlayActivated, isPressing, mouseX, mouseY, deltaX, deltaY},
    handleActivate,
    setMousePosition,
    handleDeactivate,
    setOverlayActivated,
  } = useDragAndDropReducer({
    originalTop,
    originalLeft,
  });
  const [leftScrollDiff, setLeftScrollDiff] = useState(0);
  const instrumentRef = useRef();
  const instrumentOptionsRef = useRef();
  const instrumentOptionsLeftMargin = !isLargeScreen ? 0 : INSTRUMENT_OPTIONS_LEFT_MARGIN_MAP[name];

  const showInstrumentOptions = !isPopoutMode && (!preconfigured || hasInstrumentOptions) && !isSimulateMode;
  const loaded = instrumentRef.current && (isPopoutMode || !showInstrumentOptions || instrumentOptionsRef.current);

  let mainPanel, toolbar, blueBar, panelWrapper;
  if (instrumentRef.current && instrumentRef.current.shadowRoot) {
    mainPanel = instrumentRef.current.shadowRoot.querySelector('#mainPanel');
    toolbar = mainPanel.shadowRoot.querySelector('#toolbar-container');
    blueBar = mainPanel.shadowRoot.querySelector('#titlebar-container');
    panelWrapper = mainPanel.shadowRoot.querySelector('#panel-wrapper');

    if (showInstrumentOptions) {
      toolbar.style.paddingLeft = `${55 + instrumentOptionsLeftMargin}px`;
    }
    blueBar.style.display = 'none';
  }

  const adjustStyling = () => {
    setLeftScrollDiff(panelWrapper.scrollLeft);
  };

  const reorder = ({x, y}) => {
    const newOrder = isHorizontal
      ? Math.max(Math.min(Math.floor(x / style.width), instrumentLength - 1), 0)
      : Math.max(Math.min(Math.floor(y / style.height), instrumentLength - 1), 0);
    if (order !== newOrder) {
      reorderInstruments({
        dragIdx: order,
        dropIdx: newOrder,
        size,
      });
    }
  };

  const handleMouseDown = ({pageX, pageY}) => {
    if (!isLargeScreen) return;
    handleActivate({pageX, pageY});
    setReorderingSize(size);
  };

  const handleMouseUp = () => {
    handleDeactivate();
    reorder({x: mouseX, y: mouseY});
    setReorderingSize(null);
  };

  const handleMouseMove = ({pageX, pageY}) => {
    setMousePosition({pageX, pageY});
    reorder({x: pageX, y: pageY});
  };

  const handleTouchStart = (e) => {
    handleMouseDown(e.touches[0]);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    handleMouseMove(e.touches[0]);
  };

  const handleTouchEnd = handleMouseUp;

  const getInstrumentOptionStyle = () => {
    if (!loaded || !toolbar) return {};

    const {height: toolbarHeight} = toolbar.getBoundingClientRect();
    const {height: instrumentOptionsHeight} = instrumentOptionsRef.current.getBoundingClientRect();
    return {
      left: -leftScrollDiff + instrumentOptionsLeftMargin,
      top: (toolbarHeight - instrumentOptionsHeight) / 2,
    };
  };

  const closeInstrument = () => {
    if (!instrumentOptionsRef.current) return;
    const instrumentTab = instrumentOptionsRef.current
      .closest('.bentobox-wrapper')
      .querySelector('.toolbar #instrument1')
      .shadowRoot.querySelector('#instrument-tab');
    instrumentTab.closeInstrumentWithName(name);
  };

  const handleNoScrollMinHeightUpdated = ({detail: {noScrollMinHeight: height}}) =>
    updateNoScrollMinHeight(name, height);

  const handleScroll = () =>
    instrumentRef.current.updateFloatingLocker(scrollContainerRef.current.getBoundingClientRect().top);

  useEffect(
    () => {
      if (!isPressing || !isLargeScreen) return;
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    },
    [isPressing, order, mouseX, mouseY],
  );

  useEffect(
    () => {
      if (!panelWrapper) return;
      adjustStyling();
      panelWrapper.addEventListener('scroll', adjustStyling);
      return () => {
        panelWrapper.removeEventListener('scroll', adjustStyling);
      };
    },
    [panelWrapper],
  );

  useEffect(
    () => {
      instrumentRef.current.addEventListener('noScrollMinHeightUpdated', handleNoScrollMinHeightUpdated);
      return () => {
        instrumentRef.current.removeEventListener('noScrollMinHeightUpdated', handleNoScrollMinHeightUpdated);
      };
    },
    [instrumentRef],
  );

  useEffect(
    () => {
      if (scrollContainerRef) {
        scrollContainerRef.current.addEventListener('scroll', handleScroll);
        return () => {
          scrollContainerRef.current.removeEventListener('scroll', handleScroll);
        };
      }
    },
    [scrollContainerRef],
  );

  return (
    <Motion style={isPressing ? activeStyle : defaultStyle}>
      {({scale, shadow}) => (
        <div
          className="instrument-wrapper"
          style={{
            ...style,
            width: style.width + widthDiff,
            height: isHorizontal ? style.height : style.height + heightDiff,
            left: isPressing ? mouseX - deltaX : originalLeft,
            top: (isPressing ? mouseY - deltaY : originalTop) + (isHorizontal ? 0 : topDiff),
            boxShadow: `rgba(0, 0, 0, 0.2) 0px ${shadow}px ${2 * shadow}px 0px`,
            transform: `scale(${scale})`,
            zIndex: isPressing ? 10 : 1,
          }}
        >
          {(overlayActivated || !loaded) && (
            <div className="overlay" style={!loaded ? {backgroundColor: '#f0f0f0'} : undefined} />
          )}
          {showInstrumentOptions && (
            <InstrumentOptions
              ref={{instrumentRef, instrumentOptionsRef}}
              name={name}
              isPressing={isPressing}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              style={getInstrumentOptionStyle()}
              popoutInstrument={popoutInstrument}
              closeInstrument={closeInstrument}
              isLargeScreen={isLargeScreen}
            />
          )}
          {React.createElement(INSTRUMENT_MAP[name], {
            key: settings || 'no-settings',
            ref: instrumentRef,
            class: 'instrument',
            bentoboxmode: 'true',
            settings,
          })}
          {!isPopoutMode && !isHorizontal && isLargeScreen && order + 1 < instrumentLength && (
            <ResizeHandle
              className="ns-resize-handle"
              onResize={(pageValue) => {
                const {top, bottom} = instrumentRef.current.getBoundingClientRect();
                if (pageValue <= top + 100) return;
                resizeInstrument({order, size, diff: pageValue - bottom});
              }}
              setOverlayActivated={setOverlayActivated}
            />
          )}
        </div>
      )}
    </Motion>
  );
};

Instrument.propTypes = {
  order: PropTypes.number.isRequired,
  originalLeft: PropTypes.number.isRequired,
  originalTop: PropTypes.number.isRequired,
  instrumentLength: PropTypes.number.isRequired,
  instrument: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
  }).isRequired,
  style: PropTypes.object.isRequired,
  reorderInstruments: PropTypes.func.isRequired,
  resizeInstrument: PropTypes.func.isRequired,
  popoutInstrument: PropTypes.func.isRequired,
  isHorizontal: PropTypes.bool.isRequired,
  isLargeScreen: PropTypes.bool.isRequired,
  isPopoutMode: PropTypes.bool.isRequired,
  isSimulateMode: PropTypes.bool.isRequired,
  preconfigured: PropTypes.bool.isRequired,
  updateNoScrollMinHeight: PropTypes.func.isRequired,
  setReorderingSize: PropTypes.func.isRequired,
  scrollContainerRef: PropTypes.object,
  hasInstrumentOptions: PropTypes.bool.isRequired,
};

export default Instrument;
